/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import BaseControl from 'scripts/base-control';
import BaseMap from 'scripts/models/base/map';
import BaseList from 'scripts/models/base/list';
import DomEventsManager from 'scripts/dom-events-manager';
import appContext from 'scripts/app-context';
import sessionStorage from 'scripts/services/sessionstorage-service';
import deviceUtil, { reCheckMobile } from 'scripts/utils/device';
import FeedService from 'scripts/services/base-feed-service';
import navigationListRenderer from './header-navigation-list.stache';

const alertUrl = window.globalSettings.feeds.alert;

export function customPathCompare(op1, op2) {
    const qualifyingRegexp = /qualifying/i;
    if (
        (qualifyingRegexp.test(op1) && qualifyingRegexp.test(op2)) ||
        op1 === op2
    ) {
        return 'is-active';
    }
}

export const Header = BaseControl.extend({
    init: function() {
        this._super();
        this.screen = this.options.appContext.model.screen;
        this.alertIsShown = false;
        this.alertData = null;
        this.ctaText = this.options.ctaText;
        this.ctaButton = this.options.ctaButton;

        this.alertService = new FeedService({
            feedUrl: alertUrl,
            fromCache: false,
            autoUpdate: true,
        })
            .on('data', (type, data) => {
                if (data && data.length) {
                    this.alertData = data[0];
                    this.alertIsShown = true;
                    if (reCheckMobile()) {
                        this.detectCourseAlertInMobile();
                    } else {
                        this.showCourseAlert();
                    }
                } else {
                    if (this.alertData) {
                        this.hideAlert();
                    }
                }
            })
            .on('error', (error) => {
                console.error('Error live alert data', error);
            });

        this.alertService.fetch();

        this.initNavigation();
        this.initAnimation();

        DomEventsManager.subscribe(this);
    },

    showCourseAlert: function() {
        this.element.find('.s-top-strip').addClass('s-top-strip--alert');
        this.element
            .find('.js-alert')
            .removeClass('is-hidden')
            .addClass('is-shown');
        this.element.find('.s-header-place-holder').addClass('has-alert');

        this.element
            .find('.s-top-strip__text:not(.js-alert)')
            .removeClass('is-shown')
            .addClass('is-hidden');

        if (this.alertData && this.alertData.text) {
            this.element
                .find('.s-top-strip__text.js-alert .s-string__text')
                .text(this.alertData.text);
        }

        if (this.alertData.leftButton && this.alertData.leftButton.title) {
            let link = this.element.find('.s-top-strip .s-top-strip__btn');
            this.updateLink(link, this.alertData.leftButton);
        }

        if (this.alertData.rightButton && this.alertData.rightButton.title) {
            let link = this.element.find('.s-top-strip .s-top-strip__link');
            this.updateLink(link, this.alertData.rightButton);
        }
        this.resizeHeaderPlaceholder();
        appContext.model.dispatch('header-alert-visible', [true]);
    },

    updateLink: function(element, source) {
        if (!element.hasClass('is-shown')) {
            element.addClass('is-shown');
        }
        element.attr('title', source.title);
        element.text(source.title);
        element.attr('href', source.url);
        element.attr(
            'target',
            source.target == 'newWindow' ? '_blank' : '_self',
        );
        if (element.hasClass('s-top-strip__link')) {
            element.attr('title', source.title.slice(0, 17));
            element.text(source.title.slice(0, 17));
        }
    },

    initAnimation: function() {
        this.$element
            .addClass(this.options.stickyAnimationClass)
            .attr('data-always-sticky-style', this.options.alwaysStickyStyle);
    },

    detectCourseAlertInMobile: function() {
        let mobileCourseAlertIsViewed = sessionStorage.getItem(
            'mobileCourseAlertIsViewed',
        );

        if (!mobileCourseAlertIsViewed) {
            this.showCourseAlert();
        }
    },

    onWindowScroll: function() {
        this.checkScroll();
    },

    onLayout: function() {
        this.checkScroll();
    },

    onWindowResize: function() {
        this.resizeHeaderPlaceholder();
        this.getNavigationWrapperWidth();
    },

    checkScroll: function() {
        const scrollOffset = this.screen.attr('scrollOffset');
        let offset = this.$element.offset().top + this.options.stickyThreshold;

        if (offset < 0.0) {
            offset = 0;
        }

        const hamburger = document.querySelector(
            '.m-hamburger-menu.is-expanded',
        );

        if (hamburger) {
            return;
        }

        this.$element
            .toggleClass('is-scrolled', scrollOffset > offset)
            .attr('data-scroll-direction', this.screen.attr('scrollDirection'));
    },

    initNavigation: function() {
        this.navigationModel = new BaseMap({
            width: 0,
            rootPath: this.options.pagePath,
            items: new BaseList([]),
            icons: new BaseList([]),
            options: {
                customPathCompare,
            },
        });

        this.can.batch.start();
        _.forEach(this.options.navigationItems, (item) => {
            if (item.featured && item.type === 'item') {
                item.isFit = false;
                this.navigationModel.attr('items').push(item);
            }
            if (item.display && item.type === 'icon') {
                item.isFit = false;
                this.navigationModel.attr('icons').push(item);
            }
        });

        this.can.batch.stop();

        this.$element
            .find('.s-navigation-items')
            .html(navigationListRenderer(this.navigationModel));

        this.$navigationWrapper = this.$element.find(
            '.s-navigation-items__holder',
        );
        this.$navigationWrapperItems = this.$navigationWrapper.find(
            '.s-nav-items__cell-items',
        );
        this.$navigationWrapperLogo = this.$navigationWrapper.find(
            '.s-nav-items__cell-logo',
        );
        this.$navigationWrapperIcons = this.$navigationWrapper.find(
            '.s-nav-items__cell-icons',
        );

        this.$headerPlaceholder = this.$element.find('.s-header-place-holder');
        this.$alert = this.$element.find('.js-alert');
        this.$fontOptions = this.$element.find('.js-font-options');
        this.$iconOptions = this.$element.find('.js-icon-options');
        this.$navigationMenu = this.$element.find('.s-navigation-menu');
        this.navigationModel.bind(
            'width',
            this.checkNavigationItemsCount.bind(this),
        );
        this.getNavigationWrapperWidth();
    },

    resizeHeaderPlaceholder: function() {
        let placeholderHeight = parseInt(
            this.$headerPlaceholder.css('min-height'),
        );

        if (
            this.alertIsShown &&
            ['extra-small'].indexOf(
                appContext.model.screen.attr('screenSize'),
            ) > -1
        ) {
            let alertHeight = this.$alert.hasClass('is-hidden')
                ? 0
                : this.$alert.outerHeight();
            this.$headerPlaceholder.css(
                'height',
                placeholderHeight + alertHeight,
            );
            this.$headerPlaceholder.css(
                'min-height',
                placeholderHeight + alertHeight,
            );
        }
    },

    getNavigationWrapperWidth: function() {
        this.navigationModel.attr(
            'width',
            this.$navigationWrapper.width() -
                (this.$navigationWrapperLogo.width() +
                    parseFloat(
                        this.$navigationWrapperLogo.css('padding-right'),
                    )),
        );
    },

    checkNavigationItemsCount: function(width) {
        const AVERAGE_CHAR_WIDTH_RATIO = 0.52;
        let navigationIconWidth =
            parseFloat(this.$iconOptions.css('width')) +
            parseFloat(this.$iconOptions.css('margin-left'));
        let navigationWrapperWidth = this.navigationModel.attr('width');
        let fontSize = parseFloat(this.$fontOptions.css('font-size'));
        let letterSpacing = parseFloat(this.$fontOptions.css('letter-spacing'));
        let itemPadding = parseFloat(this.$fontOptions.css('padding-left'));
        let charWidth = fontSize * AVERAGE_CHAR_WIDTH_RATIO + letterSpacing;

        let itemsWidth = 64; // Where 64 are pixels of items wrapper margin and padding
        this.navigationModel.attr('items').each((item) => {
            this.itemWidth =
                item.attr('title').length * charWidth + 2 * itemPadding;
            if (itemsWidth + this.itemWidth <= navigationWrapperWidth) {
                itemsWidth += this.itemWidth;
                item.attr('isFit', true);
            } else {
                item.attr('isFit', false);
            }
        });
        let textItemsWidth = itemsWidth;
        let visibleIcons = 0;
        this.navigationModel.attr('icons').each((item) => {
            itemsWidth += navigationIconWidth;
            if (itemsWidth <= navigationWrapperWidth) {
                visibleIcons++;
                item.attr('isFit', true);
            } else {
                item.attr('isFit', false);
            }
        });

        const navigationLogoWidth =
            this.$navigationWrapperLogo.width() +
            parseFloat(this.$navigationWrapperLogo.css('padding-right'));
        if (
            this.$navigationWrapperIcons.width() < navigationLogoWidth &&
            textItemsWidth + navigationLogoWidth * 2 <
                this.$navigationWrapper.width()
        ) {
            this.$navigationWrapperItems.css(
                'padding-right',
                navigationLogoWidth - this.$navigationWrapperIcons.width(),
            );
        } else {
            this.$navigationWrapperItems.css('padding-right', '0');
        }

        if (visibleIcons > 0) {
            this.$navigationMenu.removeClass('no-icons');
            this.$headerPlaceholder.removeClass('no-icons');
        } else {
            this.$navigationMenu.addClass('no-icons');
            this.$headerPlaceholder.addClass('no-icons');
        }
    },

    '.s-hamburger click': function() {
        appContext.model.dispatch('hamburger-menu-toggle', [true]);
    },

    '.js-close click': function(element, event) {
        event.preventDefault();
        this.hideAlert();
    },
    hideAlert: function() {
        this.$element.find('.s-top-strip').removeClass('s-top-strip--alert');
        this.$element
            .find('.s-top-strip__text')
            .toggleClass('is-shown')
            .toggleClass('is-hidden');
        this.$headerPlaceholder.removeClass('has-alert');
        this.alertIsShown = false;
        this.resizeHeaderPlaceholder();

        this.setCachedData();
        if (reCheckMobile()) {
            sessionStorage.setItem('mobileCourseAlertIsViewed', true);
        }

        appContext.model.dispatch('header-alert-visible', [false]);
    },
    setCachedData: function() {
        let ctaButton = this.element.find('.s-top-strip .s-top-strip__btn');
        if (this.ctaButton && this.ctaButton.title) {
            this.updateLink(ctaButton, this.ctaButton);
        } else {
            ctaButton.removeClass('is-shown');
        }

        let ctaText = this.element.find('.s-top-strip .s-top-strip__link');
        if (this.ctaText && this.ctaText.title) {
            this.updateLink(ctaText, this.ctaText);
        } else {
            ctaText.removeClass('is-shown');
        }
    },
    destroy: function() {
        if (this.alertService) {
            this.alertService.destroy();
            this.alertService = null;
        }
    },
});
